import React, { ReactElement, useState } from 'react';

import { Tooltip, Fab } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  DeleteForever, Add
} from "@mui/icons-material";

import { 
  Potence,
  SummonOption,
  Summon,
  CreatureLimitation,
} from "@dndModels";
import { 
  CHARACTER_LEVELS,
} from "@dndModels/Constants";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";

import DisplayObjects from "@dndComponents/display/DisplayObjects";

import SummonStatBlockInput from "@dndComponents/feature/SummonStatBlockInput";
import CreatureLimitInput from "@dndComponents/limitations/CreatureLimitInput";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import ModalOrDrawer from '@components/display/ModalOrDrawer';

interface Props {
  obj: Potence;
  slot_level: number;
  onChange: (changed: Potence) => void;
  onDelete: () => void;
  disabled: boolean;
}

export default function AbilityPotenceCreaturesInput({ 
  obj,
  slot_level,
  onChange,
  onDelete,
  disabled 
}: Props) {
  const [expanded_option, setExpandedOption] = useState("");

  const renderOption = (option: SummonOption) => {
    console.log(option);
    return (
      <Grid size={8} key="summary">
        { option.summon_type === "Custom" && option.custom_stat_block ? 
          <span>{ option.custom_stat_block.name }</span> 
        : option.creature_limit.creature_ids.length > 0 ? 
          <DisplayObjects type="creature" ids={option.creature_limit.creature_ids} /> 
        : 
          <span>
            { option.creature_limit.creature_types.join(", ") } 
            { option.creature_limit.subtypes.length > 0 && 
              <span>
                ({ option.creature_limit.subtypes.join(", ") })
              </span> 
            }
          </span>
        }
      </Grid>
    );
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer 
        open={expanded_option !== ""}
        full_page
        onClose={() => setExpandedOption("") }>
        <>
          { renderSelectedOption() }
        </>
      </ModalOrDrawer>
    );
  }

  const renderSelectedOption = () => {
    const return_us: ReactElement[] = [];
    const option = obj.summon_options.find(o => o.true_id === expanded_option);
    if (option) {
      return_us.push(
        <Grid size={12} key="custom?">
          <SelectStringBox 
            name="Summon Type"
            value={option.summon_type} 
            options={["Creature","Custom","Creature using Transform"]}
            disabled={disabled}
            onChange={(changed: "Creature" | "Custom" | "Creature using Transform") => {
              option.summon_type = changed;
              if (changed === "Custom" && !option.custom_stat_block) {
                option.custom_stat_block = new Summon();
              }
              const new_obj = obj;
              onChange(new_obj);
            }}
          />
        </Grid>
      );
      return_us.push(
        <Grid size={2} key="delete">
          { !disabled &&
            <Tooltip title={`Delete Option`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  const new_obj = obj;
                  new_obj.summon_options = new_obj.summon_options.filter(o => o.true_id !== option.true_id);
                  onChange(new_obj);
                  setExpandedOption("");
                }}>
                <DeleteForever/>
              </Fab>
            </Tooltip>
          }
        </Grid>
      );
      if (option.summon_type !== "Custom") {
        return_us.push(
          <Grid size={12} key="limit">
            <CreatureLimitInput
              obj={option.creature_limit}
              disabled={disabled}
              onChange={(changed: CreatureLimitation) => {
                option.creature_limit = changed.clone();
                const new_obj = obj;
                onChange(new_obj);
              }}
            />
          </Grid>
        );
      }
      return_us.push(
        <Grid size={4} key="count">
          <StringBox 
            value={`${option.count}`} 
            name="Count"
            type="number"
            disabled={disabled}
            onBlur={(changed: string) => {
              option.count = +changed;
              const new_obj = obj;
              onChange(new_obj);
            }}
          />
        </Grid>
      );
      if (option.summon_type === "Custom"){
        if (option.custom_stat_block) { 
          return_us.push(
            <Grid size={12} key="custom_stat_block">
              <SummonStatBlockInput 
                obj={option.custom_stat_block}
                slot_level={slot_level}
                disabled={disabled}
                onChange={(changed: Summon) => {
                  const new_obj = obj;
                  console.log(new_obj);
                  const opt_finder = new_obj.summon_options.filter(o => o.true_id === option.true_id);
                  console.log(new_obj);
                  console.log(opt_finder);
                  if (opt_finder.length === 1 && opt_finder[0].custom_stat_block) {
                    opt_finder[0].custom_stat_block.copy(changed);
                  }
                  console.log(changed);
                  onChange(new_obj);
                }}
              />
            </Grid>
          );
        }
      } else if (option.summon_type === "Creature using Transform") {
        // return_us.push(
        //   <Grid size={12} key="transform">
        //     <SelectObjectBox
        //       name="Using Transform"
        //       data_type="transform"
        //       value={option.transform_id}
        //       disabled={disabled}
        //       onChange={(changed: string) => {
        //         option.transform_id = changed;
        //         const new_obj = obj;
        //         onChange(new_obj);
        //       }}
        //       // onDelete={onDelete}
        //     />
        //   </Grid>
        // );
      }
    }
    return return_us;
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid size={4}>
        <SelectStringBox 
          options={CHARACTER_LEVELS}
          value={`${obj.level}`} 
          name="Level"
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.level = +changed;
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid size={4}>
        <span className="header">
          Options
        </span>
        { !disabled &&
          <Tooltip title={`Add Option`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              onClick={ () => {
                const new_obj = obj;
                new_obj.summon_options.push(new SummonOption());
                onChange(new_obj);
              }}>
              <Add/>
            </Fab>
          </Tooltip>
        }
      </Grid>
      <Grid size={4}>
        { !disabled &&
          <Tooltip title={`Delete Potence`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              onClick={ () => {
                onDelete();
              }}>
              <DeleteForever/>
            </Fab>
          </Tooltip>
        }
      </Grid>
      { obj.summon_options.map((option) => {
        return (
          <Grid size={12} key={option.true_id}
            container spacing={0} direction="row">
            <Grid size={2}>
              <ToggleButtonBox 
                value={option.true_id === expanded_option} 
                name="Expand"
                disabled={disabled}
                onToggle={() => {
                  setExpandedOption(expanded_option === option.true_id ? "" : option.true_id);
                }}
              />
            </Grid>
            { renderOption(option) }
          </Grid>
        );
      })}
      { renderExtras() }
    </Grid>
  );
}
